import { createSlice } from "@reduxjs/toolkit";
import {
  getNotificationTemplates,
  getNotificationTemplate,
  createNotificationTemplate,
  updateNotificationTemplate,
  deleteNotificationTemplate,
  incrementTemplateUsage,
  getTemplatesByCategory
} from "../Action/NotificationTemplateAction";

const initialState = {
  templates: [],
  currentTemplate: null,
  loading: false,
  error: null,
  totalCount: 0,
  totalPages: 0,
  currentPage: 1,
  hasNextPage: false,
  hasPrevPage: false
};

const notificationTemplateSlice = createSlice({
  name: "notificationTemplates",
  initialState,
  reducers: {
    clearCurrentTemplate: (state) => {
      state.currentTemplate = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get all templates
      .addCase(getNotificationTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNotificationTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload.data || [];
        state.totalCount = action.payload.totalItems || 0;
        state.totalPages = action.payload.totalPages || 0;
        state.currentPage = action.payload.currentPage || 1;
        state.hasNextPage = action.payload.hasNextPage || false;
        state.hasPrevPage = action.payload.hasPrevPage || false;
      })
      .addCase(getNotificationTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Get single template
      .addCase(getNotificationTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNotificationTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTemplate = action.payload.data;
      })
      .addCase(getNotificationTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Create template
      .addCase(createNotificationTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNotificationTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.templates.unshift(action.payload.data);
        state.totalCount += 1;
      })
      .addCase(createNotificationTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Update template
      .addCase(updateNotificationTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotificationTemplate.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTemplate = action.payload.data;
        const index = state.templates.findIndex(t => t._id === updatedTemplate._id);
        if (index !== -1) {
          state.templates[index] = updatedTemplate;
        }
        if (state.currentTemplate && state.currentTemplate._id === updatedTemplate._id) {
          state.currentTemplate = updatedTemplate;
        }
      })
      .addCase(updateNotificationTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Delete template
      .addCase(deleteNotificationTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteNotificationTemplate.fulfilled, (state, action) => {
        state.loading = false;
        const templateId = action.payload.templateId;
        state.templates = state.templates.filter(t => t._id !== templateId);
        state.totalCount -= 1;
        if (state.currentTemplate && state.currentTemplate._id === templateId) {
          state.currentTemplate = null;
        }
      })
      .addCase(deleteNotificationTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Increment usage
      .addCase(incrementTemplateUsage.fulfilled, (state, action) => {
        const templateId = action.payload?.templateId || action.meta?.arg;
        if (templateId) {
          const template = state.templates.find(t => t._id === templateId);
          if (template) {
            template.usageCount = (template.usageCount || 0) + 1;
            template.lastUsed = new Date().toISOString();
          }
        }
      })
      .addCase(incrementTemplateUsage.rejected, (state, action) => {
        // Silently handle usage tracking errors
        console.warn("Failed to track template usage:", action.error);
      })

      // Get by category
      .addCase(getTemplatesByCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTemplatesByCategory.fulfilled, (state, action) => {
        state.loading = false;
        // This could be used for category-specific views
      })
      .addCase(getTemplatesByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  }
});

export const { clearCurrentTemplate, clearError } = notificationTemplateSlice.actions;
export default notificationTemplateSlice.reducer;